// Strategy types
export interface Strategy {
  strategy_id: number;
  strategy_name: string;
  description: string;
}

// Instrument types
export interface Instrument {
  epic: string;
  name: string;
  type: string;
}

// Resolution types
export interface Resolution {
  value: string;
  label: string;
}

// Backtest types
export interface Backtest {
  backtest_id: number;
  strategy_id: number;
  epic: string;
  resolution: string;
  from_date?: string;
  fromDate?: string;
  to_date?: string;
  toDate?: string;
  start_date?: string;
  end_date?: string;
  total_trades: number;
  winning_trades: number;
  losing_trades: number;
  win_rate: number;
  total_profit_loss: number;
  sharpe_ratio: number | null;
  created_at?: string;
  started_at?: string;
}

// Backtest Run Request
export interface BacktestRunRequest {
  userId: number;
  strategyId: number;
  epic: string;
  resolution: string;
  fromDate: string;
  toDate: string;
  useML?: boolean;
  useMlExitPrediction?: boolean;
  useMlReversalPrediction?: boolean;
}

// Trade Detail types
export interface TradeDetail {
  trade_id?: number;
  tradeId?: number;
  backtest_id?: number;
  backtestId?: number;
  trade_number?: number;
  tradeNumber?: number;
  direction?: string;
  entry_time?: string;
  entryTime?: string;
  entry_price?: number;
  entryPrice?: number;
  exit_time?: string;
  exitTime?: string;
  exit_price?: number;
  exitPrice?: number;
  exit_reason?: string;
  exitReason?: string;
  size?: number;
  profit_loss?: number;
  profitLoss?: number;
  profit_loss_percent?: number;
  profitLossPercent?: number;
  entry_ema9?: number;
  entryEma9?: number;
  entry_ema21?: number;
  entryEma21?: number;
  entry_ema50?: number;
  entryEma50?: number;
  entry_rsi?: number;
  entryRsi?: number;
  max_profit_reached?: number;
  maxProfitReached?: number;
  max_drawdown_in_trade?: number;
  maxDrawdownInTrade?: number;
  entry_reason?: string;
  entryReason?: string;
  is_live?: boolean;
  isLive?: boolean;
  ml_approved?: boolean | null;
  mlApproved?: boolean | null;
  account_id?: string;
  accountId?: string;
  ml_model_version?: string;
  mlModelVersion?: string;
}

// Trading Account IDs — all strategies use unified CODETREND_1HMFEML account
export const TRADING_ACCOUNTS = {
  CODETREND_1HMFEML: '299082010457069070',
  MANUAL: '299087495130306062',
} as const;

export type TradingAccountId = typeof TRADING_ACCOUNTS[keyof typeof TRADING_ACCOUNTS];

// Backtest Results Response
export interface BacktestRunResult {
  backtest_id?: number;
  backtestId?: number; // Backend uses camelCase
  strategy_id?: number;
  epic?: string;
  resolution?: string;
  start_date?: string;
  startDate?: string; // Backend uses camelCase
  end_date?: string;
  endDate?: string; // Backend uses camelCase
  total_trades?: number;
  totalTrades?: number; // Backend uses camelCase
  winning_trades?: number;
  winningTrades?: number; // Backend uses camelCase
  losing_trades?: number;
  losingTrades?: number; // Backend uses camelCase
  win_rate?: number;
  winRate?: number; // Backend uses camelCase
  total_profit_loss?: number;
  totalProfitLoss?: number; // Backend uses camelCase
  sharpe_ratio?: number;
  sharpeRatio?: number; // Backend uses camelCase
  max_drawdown?: number;
  maxDrawdown?: number; // Backend uses camelCase
  max_profit?: number;
  status?: string;
  startedAt?: string;
  completedAt?: string;
  trades?: TradeDetail[]; // Backend includes trades in response
  mlStatus?: string | null;
  mlWarning?: string | null;
}

// Candle data for chart
export interface CandleData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

// Manual Trading Types
export interface TradeSignalRequest {
  signal_type: 'LONG' | 'SHORT' | 'EXIT';
  instrument: string;
  timeframe?: string;
  entry_price: number;
  exit_price?: number;
  size: number;
  rsi_value?: number;
  entry_reason?: string;
  exit_reason?: string;
  timestamp?: string;
  trade_id: string;
}

export interface TradeSignalResponse {
  status: string;
  message: string;
  trade_id?: number;
  deal_id?: string;
  instrument?: string;
  signal_type?: string;
  timestamp?: string;
  entry_price?: number;
  exit_price?: number;
}

export interface Candle {
  candle_id: number;
  epic: string;
  resolution: string;
  timestamp: string;
  open_price: number;
  high_price: number;
  low_price: number;
  close_price: number;
  volume: number;
  created_at: string;
}

// Trading Analysis Event Types
export type AnalysisEventType =
  | 'CANDLE_RECEIVED'
  | 'STRATEGY_ANALYZING'
  | 'NO_SIGNAL'
  | 'SIGNAL_DETECTED'
  | 'ML_VALIDATION_STARTED'
  | 'ML_REJECTED'
  | 'ML_APPROVED'
  | 'TRADE_PLACED'
  | 'TRADE_CLOSED'
  | 'ERROR';

export interface MlModelPrediction {
  model: string;
  prediction: boolean;
}

export interface AnalysisEventDetails {
  signalType?: string;
  signalReason?: string;
  mlConfidence?: number;
  mlModels?: MlModelPrediction[];
  tradeId?: number;
  entryPrice?: number;
  exitPrice?: number;
  profitLoss?: number;
  indicators?: { [key: string]: number };
  errorMessage?: string;
}

export interface AnalysisEvent {
  timestamp: string;
  eventType: AnalysisEventType;
  instrument: string;
  resolution: string;
  candleTime?: string;
  message: string;
  details?: AnalysisEventDetails;
}

// Console Log Types
export interface ConsoleLogMessage {
  timestamp: string;
  level: string;
  message: string;
  exception?: string;
}

// Configuration Types
export interface ConfigItem {
  configKey: string;
  configValue: string;
  valueType: string;
  category: string;
  description: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface ConfigCategory {
  category: string;
  items: ConfigItem[];
}

export interface UpdateConfigRequest {
  configKey: string;
  configValue: string;
  updatedBy?: string;
}
